"use client";

import { useEffect, useMemo, useRef } from "react";
import { useSetAtom } from "jotai";
import { cn } from "@/lib/utils";
import { floatingActions } from "@/utils/constants/layout";
import { dialogAtom } from "@/stores/dialogAtom";
import { bindScrollVisibility, scrollToTop } from "@/lib/scroll";

export default function ActionFloatingButtons() {
  const setDialog = useSetAtom(dialogAtom);
  const backToTopRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const el = backToTopRef.current;
    if (!el) return;
    return bindScrollVisibility(el, { threshold: 240 });
  }, []);

  const actionHandlers = useMemo(
    () =>
      new Map<string, () => void>([
        ["back-to-top", () => scrollToTop()],
        ["download-app", () => setDialog({ id: "download_app_dialog" })],
        ["support", () => setDialog({ id: "support_dialog" })],
      ]),
    [setDialog],
  );

  const handleActionClick = (actionId: string) => {
    actionHandlers.get(actionId)?.();
  };

  return (
    <div className="pointer-events-none fixed bottom-8 right-6 z-20 hidden flex-col gap-3 lg:flex">
      {floatingActions.map((action) => {
        const Icon = action.icon;
        const isBackToTop = action.id === "back-to-top";

        return (
          <button
            key={action.id}
            type="button"
            aria-label={action.label}
            onClick={() => handleActionClick(action.id)}
            ref={isBackToTop ? backToTopRef : undefined}
            className={cn(
              "pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full border bg-white shadow-float-panel! transition hover:bg-subtle",
              isBackToTop && "animate-in fade-in zoom-in-95 duration-200",
            )}
          >
            <Icon size={22} stroke={1.9} />
          </button>
        );
      })}
    </div>
  );
}
